import React, { useEffect } from "react";
import { NavLink, Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { Flame, LogOut, Menu, X, Lightbulb, Trophy } from "lucide-react";
import { useState } from "react";
import { api } from "@/lib/api";
import LangSwitcher from "@/components/LangSwitcher";
import InstallAppButton from "@/components/InstallAppButton";
import NotificationBell from "@/components/NotificationBell";
import UserSearchBar from "@/components/UserSearchBar";
import MobileInstallBanner from "@/components/MobileInstallBanner";
import IOSSafariBanner from "@/components/iOSSafariBanner";
import VersionBanner from "@/components/VersionBanner";
import FeedbackButton from "@/components/FeedbackButton";
import InstallBanner from "@/components/InstallBanner";
import { PV_TR } from "@/lib/pipeVizI18n";

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { lang, setLang, t } = useLang();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [unreadFb, setUnreadFb] = useState(0);
  const [pendingJobs, setPendingJobs] = useState(0);
  const [unreadChat, setUnreadChat] = useState(0);
  const [unreadDM, setUnreadDM] = useState(0);
  const [pendingMod, setPendingMod] = useState(0);

  // Admin: poll combined inbox (new feedback + pending_review jobs) every 30s
  useEffect(() => {
    if (user?.role !== "admin") { setUnreadFb(0); setPendingJobs(0); setPendingMod(0); return; }
    let cancelled = false;
    const load = () => {
      api.get("/admin/inbox-count")
        .then((r) => {
          if (cancelled) return;
          setUnreadFb(r.data?.feedback || 0);
          setPendingJobs(r.data?.pending_jobs || 0);
        })
        .catch(() => {});
      api.get("/admin/pending-actions/count")
        .then((r) => { if (!cancelled) setPendingMod(r.data?.count || 0); })
        .catch(() => {});
    };
    load();
    const id = setInterval(load, 30000);
    return () => { cancelled = true; clearInterval(id); };
  }, [user?.id, user?.role]);

  // All logged-in users: poll unread @mention count every 30s
  useEffect(() => {
    if (!user) { setUnreadChat(0); return; }
    let cancelled = false;
    const load = () => {
      api.get("/chat/notifications/unread")
        .then((r) => { if (!cancelled) setUnreadChat(r.data?.total || 0); })
        .catch(() => {});
    };
    load();
    const id = setInterval(load, 30000);
    // refresh immediately when chat page marks something read
    const onRead = () => load();
    window.addEventListener("chat:read", onRead);
    return () => { cancelled = true; clearInterval(id); window.removeEventListener("chat:read", onRead); };
  }, [user?.id]);

  // Poll unread DM count every 30s
  useEffect(() => {
    if (!user) { setUnreadDM(0); return; }
    let cancelled = false;
    const load = () => {
      api.get("/dm/unread-count")
        .then((r) => { if (!cancelled) setUnreadDM(r.data?.count || 0); })
        .catch(() => {});
    };
    load();
    const id = setInterval(load, 30000);
    const onRead = () => load();
    window.addEventListener("dm:read", onRead);
    return () => { cancelled = true; clearInterval(id); window.removeEventListener("dm:read", onRead); };
  }, [user?.id]);

  const linkClass = ({ isActive }) =>
    `px-2.5 py-1.5 text-sm whitespace-nowrap font-display uppercase tracking-wider transition-colors ${
      isActive ? "text-gold-500" : "text-zinc-400 hover:text-white"
    }`;

  return (
    <div className="min-h-screen flex flex-col bg-[var(--wb-bg)]">
      {/* iPhone non-Safari nudge banner (sticky top, dismissible for 7 days). */}
      <IOSSafariBanner />
      {/* Sub upsell banner - only for logged-in non-subscribers */}
      {user && !user.is_subscribed && (
        <div data-testid="subs-banner" className="bg-gold-600 text-white text-center text-xs md:text-sm font-mono uppercase tracking-wider py-1.5 px-4">
          <Link to="/pricing" className="inline-flex items-center gap-2 hover:underline" data-testid="subs-banner-link">
            <Flame className="w-3.5 h-3.5" />
            {lang === "sk"
              ? "Odomkni Pro: €3.99/týždeň alebo €6.99/mesiac — komunitný chat + AI 5 správ/deň →"
              : "Unlock Pro: €3.99/week or €6.99/month — community chat + AI 5 messages/day →"}
          </Link>
        </div>
      )}
      <header
        data-testid="main-header"
        className="sticky top-0 z-50 border-b border-zinc-800 backdrop-blur-xl bg-black/70"
      >
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center gap-2 md:justify-between">
          <Link to="/" className="flex items-center gap-2.5 flex-shrink-0" data-testid="brand-link">
            <img src="/tigik-logo.png" alt="Tigik" className="w-9 h-9 object-cover border border-gold-600/40" />
            <span className="font-display font-black text-xl tracking-tight">
              TIG<span className="text-gold-500">IK</span>
            </span>
            <span className="wb-tag hidden md:inline-flex">v4.4</span>
          </Link>

          {/* Mobile: hamburger centered (between logo and right actions) */}
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen(!open)}
            aria-label="Menu"
            className="md:hidden mx-auto p-2 bg-white/5 backdrop-blur border border-white/10 hover:bg-white/10 hover:border-gold-600/50 text-zinc-200 transition-all rounded-sm"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>

          <nav className="hidden md:flex items-center gap-1" data-testid="main-nav">
            <NavLink to="/" end className={linkClass} data-testid="nav-home">{t.nav.home}</NavLink>
            <NavLink to="/calculators/orbital" className={linkClass} data-testid="nav-orbital">{t.nav.orbital}</NavLink>
            <NavLink to="/calculators/manual-tig" className={linkClass} data-testid="nav-tig">{t.nav.tig}</NavLink>
            <NavLink to="/calculators/pipefitter" className={linkClass} data-testid="nav-pipefitter">{lang === "sk" ? "Potrubár" : "Pipefitter"}</NavLink>
            <NavLink to="/pipe-visualizer" className={linkClass} data-testid="nav-pipe-visualizer">{(PV_TR[lang] || PV_TR.sk).nav}</NavLink>
            <NavLink to="/ai-advisor" className={linkClass} data-testid="nav-ai">{t.nav.ai}</NavLink>
            <span className="relative inline-block">
              <NavLink to="/community" className={linkClass} data-testid="nav-community">{t.nav.community}</NavLink>
              {user && unreadChat > 0 && (
                <span
                  data-testid="nav-community-badge"
                  title={`${unreadChat} ${lang === "sk" ? "neprečítaných @zmienok" : lang === "cs" ? "nepřečtených @zmínek" : lang === "de" ? "ungelesene @-Erwähnungen" : lang === "pl" ? "nieprzeczytanych @wzmianek" : "unread @mentions"}`}
                  className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 text-[10px] font-bold bg-red-600 text-white rounded-full inline-flex items-center justify-center animate-pulse pointer-events-none"
                >
                  {unreadChat > 99 ? "99+" : unreadChat}
                </span>
              )}
            </span>
            <NavLink to="/jobs" className={linkClass} data-testid="nav-jobs">{{ sk:"Inzercia", cs:"Inzerce", en:"Ads", de:"Anzeigen", pl:"Ogłoszenia" }[lang] || "Ads"}</NavLink>
            {user && <NavLink to="/history" className={linkClass} data-testid="nav-history">{lang === "sk" ? "História" : "History"}</NavLink>}
            <NavLink to="/pricing" className={linkClass} data-testid="nav-pricing">{t.nav.pricing}</NavLink>
            <NavLink
              to="/quiz"
              data-testid="nav-quiz"
              className={({ isActive }) =>
                `px-3 py-1.5 text-xs font-mono uppercase tracking-wider transition-colors inline-flex items-center gap-1.5 ${
                  isActive ? "text-cyan-300" : "text-cyan-400 hover:text-cyan-300"
                }`
              }
              title={lang === "sk" ? "Otestuj sa — 7 dní ACTIVE grátis" : "Quiz — 7 days ACTIVE free"}
            >
              <Lightbulb className="w-3 h-3" strokeWidth={2.2} />
              {lang === "sk" ? "Kvíz" :
               lang === "cs" ? "Kvíz" :
               lang === "de" ? "Quiz" :
               lang === "pl" ? "Quiz" :
               "Quiz"}
            </NavLink>
            <NavLink
              to="/wall-of-fame"
              data-testid="nav-wall-of-fame"
              className={({ isActive }) =>
                `px-3 py-1.5 text-xs font-mono uppercase tracking-wider transition-colors inline-flex items-center gap-1.5 ${
                  isActive ? "text-gold-300" : "text-gold-400 hover:text-gold-300"
                }`
              }
              title={lang === "sk" ? "Sieň Slávy SauberEko" : "SauberEko Hall of Fame"}
            >
              <Trophy className="w-3 h-3" strokeWidth={2.2} />
              {lang === "sk" ? "Sieň Slávy" :
               lang === "cs" ? "Síň Slávy" :
               lang === "de" ? "Hall of Fame" :
               lang === "pl" ? "Sala Sławy" :
               "Hall of Fame"}
            </NavLink>
            {(user?.role === "admin" || user?.role === "moderator") && (
              <NavLink to="/admin" className={linkClass} data-testid="nav-admin" onClick={() => { try { if (user?.role === "admin" && pendingMod > 0) sessionStorage.setItem("admin_tab", "pending"); } catch (e) {} }}>
                <span className="relative inline-flex items-center">
                  {user?.role === "moderator" ? "Vedúcko" : "Admin"}
                  {user?.role === "admin" && pendingMod > 0 && (
                    <span data-testid="header-mod-pending-badge" className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold bg-red-600 text-white rounded-full animate-pulse" title={`${pendingMod} čaká schválenie od Vedúčka`}>
                      {pendingMod > 99 ? "99+" : pendingMod}
                    </span>
                  )}
                </span>
              </NavLink>
            )}
            {user?.role === "admin" && (() => {
              const totalInbox = unreadFb + pendingJobs;
              const priorityTab = pendingJobs > 0 ? "jobs" : "feedback";
              const priorityHref = pendingJobs > 0 ? "/admin?tab=jobs" : "/admin?tab=feedback";
              const titleParts = [];
              if (pendingJobs > 0) titleParts.push(`${pendingJobs} ${pendingJobs === 1 ? "inzerát čaká schválenie" : "inzerátov čaká schválenie"}`);
              if (unreadFb > 0) titleParts.push(`${unreadFb} nových nápadov`);
              const title = totalInbox > 0 ? titleParts.join(" · ") : "Admin schránka";
              return (
                <Link
                  to={priorityHref}
                  onClick={() => {
                    try { sessionStorage.setItem("admin_tab", priorityTab); } catch (e) { /* ignore */ }
                  }}
                  data-testid="header-feedback-bell"
                  title={title}
                  className="relative inline-flex items-center justify-center w-9 h-9 rounded-md hover:bg-zinc-800 transition-colors"
                >
                  <Lightbulb className={`w-4 h-4 ${totalInbox > 0 ? "text-yellow-400" : "text-zinc-500"}`} strokeWidth={2} />
                  {totalInbox > 0 && (
                    <span
                      data-testid="header-feedback-badge"
                      className={`absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 text-[10px] font-bold text-white rounded-full inline-flex items-center justify-center animate-pulse ${pendingJobs > 0 ? "bg-amber-500" : "bg-red-600"}`}
                    >
                      {totalInbox > 99 ? "99+" : totalInbox}
                    </span>
                  )}
                </Link>
              );
            })()}
          </nav>

          <div className="flex items-center gap-2">
            <InstallAppButton className="hidden md:inline-flex" />
            {user && <UserSearchBar />}
            <NotificationBell />
            <LangSwitcher />
            {user ? (
              <div className="flex items-center gap-2">
                <Link
                  to="/profile"
                  data-testid="profile-link"
                  className="relative hidden md:flex items-center gap-2 px-3 py-1.5 bg-zinc-900/60 border border-zinc-700 hover:border-gold-600 transition-colors"
                >
                  <div className="w-7 h-7 bg-gold-600/20 border border-gold-600 flex items-center justify-center overflow-hidden">
                    {user.picture ? (
                      <img src={user.picture} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-xs font-display font-bold text-gold-400 uppercase">{user.name?.[0] || "?"}</span>
                    )}
                  </div>
                  <div className="leading-tight">
                    <div className="font-display font-bold text-sm tracking-wide text-white">{user.name}</div>
                    <div className="text-[9px] font-mono uppercase tracking-wider text-zinc-500 flex items-center gap-1">
                      {user.role === "admin" ? <span className="text-gold-300">ADMIN</span> : user.is_subscribed ? <span className="text-gold-400">{(user.plan_tier || "PRO").toUpperCase()}</span> : <span>FREE</span>}
                    </div>
                  </div>
                  {unreadDM > 0 && (
                    <span data-testid="profile-dm-badge"
                      title={`${unreadDM} ${lang === "sk" ? "neprečítaných správ" : "unread messages"}`}
                      className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 text-[10px] font-bold bg-red-600 text-white rounded-full inline-flex items-center justify-center animate-pulse">
                      {unreadDM > 99 ? "99+" : unreadDM}
                    </span>
                  )}
                </Link>
                <button
                  data-testid="logout-btn"
                  onClick={() => { logout(); nav("/"); }}
                  className="hidden md:flex items-center gap-1 px-3 py-1.5 text-sm text-zinc-400 hover:text-white transition-colors"
                >
                  <LogOut className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <>
                <Link to="/login" data-testid="login-link" className="hidden md:inline-block px-3 py-1.5 text-sm font-display uppercase tracking-wide text-zinc-300 hover:text-white">
                  {t.nav.login}
                </Link>
                <Link to="/register" data-testid="register-link" className="hidden md:inline-block wb-btn-primary px-4 py-1.5 text-sm">
                  {t.nav.register}
                </Link>
              </>
            )}
          </div>
        </div>
        {open && (
          <div className="md:hidden border-t border-zinc-800 px-4 py-3 flex flex-col gap-2 bg-black/80 backdrop-blur-xl" data-testid="mobile-nav">
            <NavLink to="/" end className={linkClass} onClick={() => setOpen(false)}>{t.nav.home}</NavLink>
            <NavLink to="/calculators/orbital" className={linkClass} onClick={() => setOpen(false)}>{t.nav.orbital}</NavLink>
            <NavLink to="/calculators/manual-tig" className={linkClass} onClick={() => setOpen(false)}>{t.nav.tig}</NavLink>
            <NavLink to="/calculators/pipefitter" className={linkClass} onClick={() => setOpen(false)}>{lang === "sk" ? "Potrubár" : "Pipefitter"}</NavLink>
            <NavLink to="/pipe-visualizer" className={linkClass} onClick={() => setOpen(false)} data-testid="mobile-nav-pipe-visualizer">{(PV_TR[lang] || PV_TR.sk).nav}</NavLink>
            <NavLink to="/ai-advisor" className={linkClass} onClick={() => setOpen(false)}>{t.nav.ai}</NavLink>
            <NavLink to="/community" className={linkClass} onClick={() => setOpen(false)}>
              {t.nav.community}
              {user && unreadChat > 0 && (
                <span data-testid="mobile-nav-community-badge"
                  className="ml-2 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold bg-red-600 text-white rounded-full animate-pulse">
                  {unreadChat > 99 ? "99+" : unreadChat}
                </span>
              )}
            </NavLink>
            <NavLink to="/jobs" className={linkClass} onClick={() => setOpen(false)}>{{ sk:"Inzercia", cs:"Inzerce", en:"Ads", de:"Anzeigen", pl:"Ogłoszenia" }[lang] || "Ads"}</NavLink>
            {user && <NavLink to="/history" className={linkClass} onClick={() => setOpen(false)}>{lang === "sk" ? "História" : "History"}</NavLink>}
            <NavLink to="/pricing" className={linkClass} onClick={() => setOpen(false)}>{t.nav.pricing}</NavLink>
            <NavLink
              to="/quiz"
              data-testid="mobile-nav-quiz"
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `px-3 py-1.5 text-xs font-mono uppercase tracking-wider transition-colors inline-flex items-center gap-1.5 ${
                  isActive ? "text-cyan-300" : "text-cyan-400 hover:text-cyan-300"
                }`
              }
            >
              <Lightbulb className="w-3 h-3" strokeWidth={2.2} />
              {lang === "sk" ? "Kvíz — 7 dní ACTIVE grátis" :
               lang === "cs" ? "Kvíz — 7 dní ACTIVE zdarma" :
               lang === "de" ? "Quiz — 7 Tage ACTIVE gratis" :
               lang === "pl" ? "Quiz — 7 dni ACTIVE za darmo" :
               "Quiz — 7 days ACTIVE free"}
            </NavLink>
            <NavLink
              to="/wall-of-fame"
              data-testid="mobile-nav-wall-of-fame"
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `px-3 py-1.5 text-xs font-mono uppercase tracking-wider transition-colors inline-flex items-center gap-1.5 ${
                  isActive ? "text-gold-300" : "text-gold-400 hover:text-gold-300"
                }`
              }
            >
              <Trophy className="w-3 h-3" strokeWidth={2.2} />
              {lang === "sk" ? "Sieň Slávy SauberEko" :
               lang === "cs" ? "Síň Slávy SauberEko" :
               lang === "de" ? "SauberEko Hall of Fame" :
               lang === "pl" ? "Sala Sławy SauberEko" :
               "SauberEko Hall of Fame"}
            </NavLink>
            {(user?.role === "admin" || user?.role === "moderator") && <NavLink to="/admin" className={linkClass} onClick={() => setOpen(false)}>{user?.role === "moderator" ? "Vedúcko" : "Admin"}</NavLink>}
            {user ? (
              <>
                <NavLink to="/profile" className={linkClass} onClick={() => setOpen(false)}>
                  {t.nav.profile}
                  {unreadDM > 0 && (
                    <span data-testid="mobile-profile-dm-badge"
                      className="ml-2 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold bg-red-600 text-white rounded-full animate-pulse">
                      {unreadDM > 99 ? "99+" : unreadDM}
                    </span>
                  )}
                </NavLink>
                <button className="text-left px-3 py-1.5 text-zinc-400" onClick={() => { logout(); setOpen(false); nav("/"); }}>{t.nav.logout}</button>
              </>
            ) : (
              <>
                <NavLink to="/login" className={linkClass} onClick={() => setOpen(false)}>{t.nav.login}</NavLink>
                <NavLink to="/register" className={linkClass} onClick={() => setOpen(false)}>{t.nav.register}</NavLink>
              </>
            )}
            <div className="pt-3 mt-2 border-t border-zinc-800 flex items-center gap-2">
              <InstallAppButton />
            </div>
          </div>
        )}
      </header>

      <main className="flex-1">{children}</main>

      <footer className="border-t border-zinc-800 mt-12">
        <div className="max-w-7xl mx-auto px-6 py-8 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div className="flex items-center gap-2">
            <img src="/tigik-logo.png" alt="Tigik" className="w-6 h-6 object-cover border border-zinc-700" />
            <span className="font-display font-bold text-sm tracking-tight">TIGIK</span>
            <span className="text-xs text-zinc-500 font-mono ml-2">© 2026</span>
          </div>
          <div className="text-xs text-zinc-500 font-mono uppercase tracking-wider flex items-center gap-3 flex-wrap">
            <Link to="/quiz" className="hover:text-gold-400 transition-colors" data-testid="footer-quiz">Kvíz</Link>
            <span className="text-zinc-700">·</span>
            <Link to="/wall-of-fame" className="hover:text-gold-400 transition-colors" data-testid="footer-wall-of-fame">Sieň slávy</Link>
            <span className="text-zinc-700">·</span>
            <Link to="/faq" className="hover:text-gold-400 transition-colors" data-testid="footer-faq">Časté otázky</Link>
            <span className="text-zinc-700">·</span>
            <Link to="/privacy" className="hover:text-gold-400 transition-colors" data-testid="footer-privacy">Súkromie</Link>
            <span className="text-zinc-700">·</span>
            <Link to="/terms" className="hover:text-gold-400 transition-colors" data-testid="footer-terms">Podmienky</Link>
            <span className="text-zinc-700">·</span>
            <Link to="/community-rules" className="hover:text-gold-400 transition-colors" data-testid="footer-community-rules">Pravidlá komunity</Link>
            <span className="text-zinc-700">·</span>
            <span>{t.footer?.tag || "Built for welders. By welders."}</span>
          </div>
        </div>
      </footer>
      <MobileInstallBanner />
      <VersionBanner />
      <FeedbackButton />
      <InstallBanner />
    </div>
  );
}
